import {Injectable, OnDestroy} from '@angular/core';
import {Activity} from '@docma-mm-core/activity/activity';
import {Attachment, AttachmentSource} from '@docma-mm-core/attachment/attachment';
import {AttachmentLoadingTypeEnum} from '@docma-mm-core/attachment/attachment-loading-type.enum';
import {AttachmentService} from '@docma-mm-core/attachment/attachment.service';
import {Fault, ViewFault} from '@docma-mm-core/fault/fault';
import {FaultService} from '@docma-mm-core/fault/fault.service';
import {FaultSyncService, SyncStateChange} from '@docma-mm-core/fault/sync/fault-sync.service';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import {Project} from '@docma-mm-core/project/project';
import {PendingService} from '@mydocma/app-container';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {TranslateService} from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, filter, firstValueFrom, map, Subject} from 'rxjs';
import {MmViewService} from '../mm-view.service';
import {MmFaultsLoaderService} from './mm-faults-loader.service';
import {getError} from '@docma-mm-core/utils/utils';
import {User} from '@docma-mm-core/user/user';
import {FaultPersistenceService} from '@docma-mm-core/fault/fault-persistence.service';
import {LoadLayoutFaultsOptions} from './load-layout-faults-options.enum';
import {FieldPropertyEnum} from '@docma-mm-core/field/field-property.enum';
import {MmProjectLoaderService} from '../mm-project/mm-project-loader.service';


@UntilDestroy()
@Injectable()
export class MmFaultsSyncService implements OnDestroy {

  private syncFaultSubjectsMap = new Map<string, BehaviorSubject<boolean>>();
  private project: Project;
  private syncNewFaultSubject = new Subject<ViewFault>();
  public onNewFaultSynced = this.syncNewFaultSubject.asObservable();

  constructor(private readonly faultSyncService: FaultSyncService,
              private readonly pendingService: PendingService,
              private readonly attachmentService: AttachmentService,
              private readonly viewService: MmViewService,
              private readonly faultService: FaultService,
              private readonly notificationService: NotificationService,
              private readonly faultsLoaderService: MmFaultsLoaderService,
              private readonly persistenceService: FaultPersistenceService,
              private readonly translateService: TranslateService,
              private readonly projectLoaderService: MmProjectLoaderService) {


    this.viewService.currentProject$
      .pipe(untilDestroyed(this))
      .subscribe(p => this.project = p);

    this.faultSyncService.syncFaultsState$
      .pipe(untilDestroyed(this))
      .subscribe(s => this.onFaultsSyncStateChanged(s));
  }

  ngOnDestroy(): void {
  }

  async syncFaults(faults: ViewFault[]) {

    if (faults.length > 0) {
      await this.faultsLoaderService.loadFaults(faults.filter(f => f.loadState.status !== LoadStatusEnum.NotLoaded), true);
      (await this.faultsLoaderService.loadLayoutFaults(faults.filter(f => f.layoutState.loadState.status !== LoadStatusEnum.NotLoaded), true, false, LoadLayoutFaultsOptions.EnsureVisibleAfterLoad)).subscribe();

      const hasSyncError = false;

      // this.faultSyncService.addFaultsToSyncQueue(faults[0].project.MyDocmaUid, faults);

      if (hasSyncError) {
        throw new Error('Sync error');
      }
    }
  }

  private async onFaultsSyncStateChanged(params: {changes: SyncStateChange[], startingFaultUids: string[], notifyError: boolean, modifiedOn?: Date, modifier?: User}) {
    const sSyncing = params.changes.filter(x => x.syncState && x.syncState.status === SyncStatusEnum.Syncing);
    const sError = params.changes.filter(x => x.syncState && x.syncState.status === SyncStatusEnum.Error);
    const sSynced = params.changes.filter(x => x.syncState && x.syncState.status === SyncStatusEnum.Synced);

    (sSyncing ?? []).forEach(s0 => this.startPending(s0.faultUid));
    let hasInternalErrors = false;

    if (sError && sError.length > 0) {
      for (let e of sError) {
        if (e.syncState?.error) {
          try {
            const error = getError(e.syncState?.error);
            if (error?.InternalErrors) {
              for (const errorKey in error.InternalErrors) {
                hasInternalErrors = true;
                if (params.notifyError || (params.startingFaultUids && params.startingFaultUids.findIndex(f => f === e.faultUid) !== -1)) {
                  this.notificationService.error(error.InternalErrors[errorKey].Message, this.translateService.instant('MM.fault.faultChangeError'));
                }
              }
            }
          } catch (err) {
            hasInternalErrors = false;
          }
        }
      }

      if (!hasInternalErrors && params.notifyError) {
        this.notificationService.error(this.translateService.instant('MM.fault.faultChangeError'), this.translateService.instant('MM.fault.syncError'));
      }

      if (sError && sError.length > 0) {
        sError.forEach(s1 => this.stopPending(s1.faultUid));

        if (sError[0].mydocmaUid === this.project?.MyDocmaUid) {
          this.faultsSynced(this.project?.MyDocmaUid, sError.map(x => x.faultUid));
        }
      }
    }

    if (sSynced && sSynced.length > 0) {
      sSynced.forEach(s1 => this.stopPending(s1.faultUid));

      // const s1MyDocmaUid = new Set([...sSynced.map(x => x.mydocmaUid)]);
      if (sSynced[0].mydocmaUid === this.project?.MyDocmaUid) {
        this.faultsSynced(this.project?.MyDocmaUid, sSynced.map(x => x.faultUid), params.modifiedOn, params.modifier);
      }
    }
  }

  private stopPending(faultUid: string) {
    const faultSubject = this.syncFaultSubjectsMap.get(faultUid);
    if (faultSubject) {
      faultSubject.next(false);
      this.pendingService.removePendingState(faultSubject);
    }
  }

  private startPending(faultUid: string) {
    let faultSubject = this.syncFaultSubjectsMap.get(faultUid);
    if (!faultSubject) {
      faultSubject = new BehaviorSubject<boolean>(true);
      this.syncFaultSubjectsMap.set(faultUid, faultSubject);
    }
    faultSubject.next(true);
    this.pendingService.registerPendingState(faultSubject);
  }

  private async faultsSynced(mydocamaUid: string, faultUids: string[], modifiedOn?: Date, modifier?: User): Promise<any> {
    const viewFaults = this.faultsLoaderService.getFaults(faultUids);
    if (viewFaults) {
      /* load sync status + validate change status */
      const saved = await this.faultService.getFaults(mydocamaUid, viewFaults, faultUids);
      for (const fault of saved) {
        if (modifiedOn && modifier && fault.data && Object.keys(fault.data).length > 0 && (!fault.data.ModifiedOn || fault.data.ModifiedOn < modifiedOn)) {
          fault.data.ModifiedOn = modifiedOn;
          fault.data.Modifier = modifier;
          if (fault.loadState.data && Object.keys(fault.loadState.data).length > 0) {
            fault.loadState.data.ModifiedOn = modifiedOn;
            fault.loadState.data.Modifier = modifier;
          }
          await this.persistenceService.save(mydocamaUid, fault);
        }

        const viewFault: ViewFault = viewFaults.find(x => x.uid === fault.uid);
        if (viewFault && viewFault.unsavedNew) {
          // if (!!viewFault.unsavedNew && !viewFault.id && !!fault.id && fault.syncState.status === SyncStatusEnum.Synced) {
          // }
          viewFault.id = fault.id;
          viewFault.unsavedNew = false;

          if (fault.loadState.data) {
            viewFault.loadState = fault.loadState;
          }
          viewFault.changeState = fault.changeState;
          viewFault.syncState = fault.syncState;

          this.mergeAttachments(mydocamaUid, viewFault, fault.attachments);
          this.mergeActivities(mydocamaUid, viewFault, fault.activities);

          if (!!viewFault.id) {
            await this.faultsLoaderService.loadFaults([viewFault], true).toPromise();
          }

          const fieldDict = Object.fromEntries(await firstValueFrom(this.projectLoaderService.fields
            .pipe(
              filter(f => f.loadState.status === LoadStatusEnum.Loaded),
              map(m => {
                return m.data.filter(x => x.Id != null && x.Prop === FieldPropertyEnum.Number1).map(x => [x.Prop, x]);
              }),
            )));

          if (this.project?.textPinEnabled &&
            this.project?.textPinFieldId &&
            fieldDict.hasOwnProperty(FieldPropertyEnum.Number1) &&
            fieldDict[FieldPropertyEnum.Number1].FieldId === +this.project?.textPinFieldId) {
            viewFault.pinText = viewFault.data.Number1 + '';
          }

          this.syncNewFaultSubject.next(viewFault);

          this.faultService.faultChangedSubject.next(viewFault);
        }
        else if (viewFault) {
          if (!viewFault || !viewFault.loadState || !fault || !fault?.loadState) {
            console.log('ERR');
            console.log(fault);
            console.log(viewFault);
          }
          if (fault.loadState.data) {
            viewFault.loadState = fault.loadState;
          }
          else if (viewFault.loadState.data && Object.keys(viewFault.loadState.data).length > 0 && modifiedOn) {
            viewFault.loadState.data.ModifiedOn = new Date(modifiedOn)
            viewFault.loadState.data.Modifier = modifier
          }
          viewFault.changeState = fault.changeState;
          viewFault.syncState = fault.syncState;
          this.mergeAttachments(mydocamaUid, viewFault, fault.attachments);
          this.mergeActivities(mydocamaUid, viewFault, fault.activities);

          /* notify to sync-cell-renderer and plan view */
          this.faultService.faultChangedSubject.next(viewFault);
        }
      }
      (await this.faultsLoaderService.loadLayoutFaults(viewFaults.filter(x => !!x.id), true, true, LoadLayoutFaultsOptions.EnsureVisibleAfterLoad)).subscribe();
    }
  }

  private mergeAttachments(mydocamaUid: string, model: {attachments: Array<Attachment>, addAttachment(a: Attachment)}, attachments: Array<Attachment>) {
    model.attachments.forEach(a => {
      const sa = attachments.find(at => at.uid === a.uid );
      if (sa) {
        const isNew = !a.id;
        a.loadState = sa.loadState;
        a.changeState = sa.changeState;
        a.syncState = sa.syncState;

        if (isNew && a.data.MimeType === 'application/pdf') {
          // force load thumbnail
          a.thumbnail = new AttachmentSource();
          this.attachmentService.loadAttachmentThumbnail(mydocamaUid, a , null , AttachmentLoadingTypeEnum.FromServer , true);
        }
      }
    });
    attachments.forEach(a => {
      const sa = model.attachments.find(at => at.uid === a.uid );
      if (!sa) {
        model.addAttachment(a);
      }
    });
  }


  private mergeActivities(mydocamaUid: string, model: Fault, activities: Array<Activity>) {
    model.activities.forEach(activity => {
      const sa = activities.find(ac => ac.uid === activity.uid );
      if (sa) {
        activity.loadState = sa.loadState;
        activity.changeState = sa.changeState;
        activity.syncState = sa.syncState;

        this.mergeAttachments(mydocamaUid, activity, sa.attachments);
      }
    });

    activities.forEach(activity => {
      const sa = model.activities.find(at => at.uid === activity.uid );
      if (!sa) {
        model.addActivity(activity);
      }
    });
  }

}
